import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import Button from "@mui/joy/Button";
import ToggleButtonGroup from "@mui/joy/ToggleButtonGroup";
import React from "react";
import { MilliDelta } from "../../../../../models/common";
import {
  ApproximateTime,
  ExactTime,
  QualitativeTimeDelta,
  QuantitativeTimeDelta,
  RelativeTime,
  TemporalPrecision,
  TimeInterval,
  TimeReference,
  TimeType,
} from "../../../../../models/human_time";
import {
  isApproximateTime,
  isFuturRelativeTime,
  isPastRelativeTime,
  isRelativeTime,
  isTimeInterval,
} from "../../../../../shared/utils/fact";
import { generateApproximateTime, unixToDayjs } from "../../../../../shared/utils/time";
import { useNote } from "../../../contexts";
import { ComplexDatePicker } from "../../Datepickers/ComplexDatePicker";
import RelativeDatePicker from "../../Datepickers/RelativeDatePicker";
import EditionBox from "./EditionBox";

export enum Temporality {
  PAST = "Past",
  PRESENT = "Present",
  FUTURE = "Future",
  PRECISE = "Precise",
}

interface TemporalityEditProps {
  readonly time: ApproximateTime | ExactTime | RelativeTime | TimeInterval | null | undefined;
  readonly onChange: (time: ApproximateTime | ExactTime | RelativeTime | TimeInterval) => void;
}

export function TemporalityEdit({ time, onChange }: TemporalityEditProps) {
  type TemporalityLabel = {
    title: string;
    defaultValue: ApproximateTime | RelativeTime;
  };
  const temporalityLabels: TemporalityLabel[] = [
    {
      title: Temporality.PAST,
      defaultValue: {
        type: TimeType.RELATIVE,
        reference: TimeReference.NOTE,
        delta: QualitativeTimeDelta.BEFORE,
      } as RelativeTime,
    },
    {
      title: Temporality.PRESENT,
      defaultValue: {
        type: TimeType.RELATIVE,
        reference: TimeReference.NOTE,
        delta: {
          value: 0,
          precision: TemporalPrecision.DAY,
        } as QuantitativeTimeDelta,
      } as RelativeTime,
    },
    {
      title: Temporality.FUTURE,
      defaultValue: {
        type: TimeType.RELATIVE,
        reference: TimeReference.NOTE,
        delta: QualitativeTimeDelta.AFTER,
      } as RelativeTime,
    },
    {
      title: Temporality.PRECISE,
      defaultValue: generateApproximateTime(),
    },
  ];

  const { note } = useNote();

  // Recalculate temporality based on the current time value.
  const determineTemporality = (time: any): Temporality => {
    if (isTimeInterval(time)) {
      if (isPastRelativeTime(time.begin)) return Temporality.PAST;
      if (isFuturRelativeTime(time.end)) return Temporality.FUTURE;
      return Temporality.PRECISE;
    }
    if (isPastRelativeTime(time)) return Temporality.PAST;
    if (isFuturRelativeTime(time)) return Temporality.FUTURE;
    if (isApproximateTime(time)) return Temporality.PRECISE;
    return Temporality.PRESENT;
  };

  const [temporality, setTemporality] = React.useState<Temporality>(determineTemporality(time));

  // Update temporality state if the passed "time" prop changes.
  React.useEffect(() => {
    setTemporality(determineTemporality(time));
  }, [time]);

  const updateTemporality = (newTemporality: Temporality) => {
    setTemporality(newTemporality);

    if (newTemporality === Temporality.PRESENT) {
      handlePresentTemporality();
    } else if (newTemporality === Temporality.PRECISE) {
      handlePreciseTemporality();
    } else if (newTemporality === Temporality.PAST || newTemporality === Temporality.FUTURE) {
      handlePastOrFutureTemporality(newTemporality);
    }
  };

  const handlePresentTemporality = () => {
    onChange({
      type: TimeType.RELATIVE,
      reference: TimeReference.NOTE,
      delta: {
        value: 0,
        precision: TemporalPrecision.DAY,
      } as QuantitativeTimeDelta,
    } as RelativeTime);
  };

  const handlePreciseTemporality = () => {
    onChange(generateApproximateTime());
  };

  const handlePastOrFutureTemporality = (newTemporality: Temporality) => {
    if (isRelativeTime(time)) {
      updateExistingRelativeTime(newTemporality);
    } else {
      createNewRelativeTime(newTemporality);
    }
  };

  const updateExistingRelativeTime = (newTemporality: Temporality) => {
    const updatedTime = { ...time } as RelativeTime;
    let delta = updatedTime.delta;

    if (typeof delta !== "string") {
      if (delta.value === 0) {
        updatedTime.delta =
          newTemporality === Temporality.PAST
            ? QualitativeTimeDelta.BEFORE
            : QualitativeTimeDelta.AFTER;
      } else {
        delta.value = -delta.value as MilliDelta;
      }
    } else {
      updatedTime.delta =
        delta === QualitativeTimeDelta.BEFORE
          ? QualitativeTimeDelta.AFTER
          : QualitativeTimeDelta.BEFORE;
    }

    onChange(updatedTime);
  };

  const createNewRelativeTime = (newTemporality: Temporality) => {
    onChange({
      type: TimeType.RELATIVE,
      reference: TimeReference.NOTE,
      delta:
        newTemporality === Temporality.PAST
          ? QualitativeTimeDelta.BEFORE
          : QualitativeTimeDelta.AFTER,
    } as RelativeTime);
  };

  return (
    <EditionBox title="Temporality" icon={<AccessTimeFilledIcon />}>
      <ToggleButtonGroup
        value={temporality}
        onChange={(event, newValue) => {
          newValue && updateTemporality(newValue);
        }}
      >
        {temporalityLabels.map((item) => (
          <Button key={item.title} value={item.title}>
            {item.title}
          </Button>
        ))}
      </ToggleButtonGroup>
      {temporality === Temporality.PRECISE && (
        <ComplexDatePicker
          date={time}
          note_datetime={unixToDayjs(note?.note_datetime)}
          onDateChange={onChange}
        />
      )}
      {[Temporality.PAST, Temporality.FUTURE].includes(temporality) && (
        <RelativeDatePicker
          time={time as RelativeTime | TimeInterval}
          temporality={temporality}
          onUpdate={(time) => {
            onChange(time);
          }}
        />
      )}
    </EditionBox>
  );
}
